import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
 import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Plus, Search, Eye, Edit, Trash2, Shield, Calendar, Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { roleApiService, Role } from '../services';
import { PermissionGate } from './ui/PermissionGate';

export function RoleListingPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  
  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  // Role management route (from backend permissions)
  const ROLE_ROUTE = '/roles';

  // Load roles on component mount
  useEffect(() => {
    loadRoles();
  }, [currentPage, pageSize]);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const response = await roleApiService.getRoles();
      
      if (response.success && response.data) {
        setRoles(response.data);
        setTotalCount(response.data.length);
      } else {
        toast.error(response.message || 'Failed to load roles');
      }
    } catch (error) {
      // console.error('Error loading roles:', error);
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      loadRoles();
      return;
    }

    try {
      setLoading(true);
      const response = await roleApiService.searchRoles(searchTerm, currentPage, pageSize);
      
      if (response.success && response.data) {
        setRoles(response.data);
        setTotalCount(response.data.length);
      } else {
        toast.error(response.message || 'Search failed');
      }
    } catch (error) {
      // console.error('Error searching roles:', error);
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!roleToDelete) return;

    try {
      setLoading(true);
      await roleApiService.deleteRole(roleToDelete.id.toString());
      
      // Reload roles after deletion
      await loadRoles();
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
    } catch (error) {
      // console.error('Error deleting role:', error);
      toast.error('Failed to delete role');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (role: Role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (isActive: boolean) => {
    return (
      <Badge 
        variant={isActive ? "default" : "secondary"}
        className={isActive ? "bg-green-500" : "bg-gray-400"}
      >
        {isActive ? "Active" : "Inactive"}
      </Badge>
    );
  };

  if (loading && roles.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading roles...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Role Management</h1>
          <p className="text-gray-600">Manage system roles and permissions</p>
        </div>
        <PermissionGate route={ROLE_ROUTE} action="create">
          <Button 
            onClick={() => navigate('/roles/new')}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add New Role
          </Button>
        </PermissionGate>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary-700" />
            Search & Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search roles by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="max-w-md"
              />
            </div>
            <Button onClick={handleSearch} variant="outline">
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
            {searchTerm && (
              <Button onClick={() => { setSearchTerm(''); loadRoles(); }} variant="ghost">
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Roles Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary-700" />
            Roles ({totalCount})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {roles.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Shield className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p>No roles found</p>
              {searchTerm && (
                <p className="text-sm">Try adjusting your search criteria</p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {roles.map((role) => (
                    <TableRow key={role.id}>
                      <TableCell className="font-medium">{role.name}</TableCell>
                      <TableCell className="max-w-xs truncate" title={role.description}>
                        {role.description}
                      </TableCell>
                      <TableCell>{getStatusBadge(role.isActive)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <PermissionGate route={ROLE_ROUTE} action="view">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => navigate(`/roles/${role.id}/view`)}
                              title="View Role"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={ROLE_ROUTE} action="edit">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => navigate(`/roles/${role.id}/edit`)}
                              title="Edit Role"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={ROLE_ROUTE} action="delete">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openDeleteDialog(role)}
                              title="Delete Role"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the role "{roleToDelete?.name}" and remove it from the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteRole}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
